<template>
  <GraphqlDataTable
    :query="query"
    :order-by="initialOrder"
    :column-black-list="['id']"
    :filter-black-list="['client', 'accounts']"
    :additional-query-variables="{ client: clientCompanyId }"
    :search-placeholder="$t('Search staffing agencies…')"
    :bulk-actions="bulkActions"
    max-height-adjustment="15vh"
  >
    <template #action="{ row }">
      <RecruiterLinkActions :row="row" @unlink="pendingRemoval = $event" />
    </template>
    <template #full-screen-globals>
      <UnlinkRecruiterModal
        v-if="pendingRemoval"
        show
        :link="pendingRemoval"
        :client-name="clientName"
        @cancel="pendingRemoval = null"
        @removed="pendingRemoval = null"
      />
      <LinkRecruitersModal
        v-if="linking"
        :show="linking"
        mode="manage"
        :clients="[{ id: clientCompanyId, name: clientName }]"
        @update:show="linking = $event"
      />
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsIconNameEnum } from '@worksome/wds-components'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import LinkRecruitersModal from '@/components/Clients/LinkRecruitersModal.vue'
import RecruiterLinkActions from '@/components/Clients/RecruiterLinkActions.vue'
import UnlinkRecruiterModal from '@/components/Clients/UnlinkRecruiterModal.vue'
import type { PendingSupplierUnlink } from '@/components/Clients/supplierLinkUnlink'
import { SUPPLIER_LINK_QUERIES } from '@/components/Clients/supplierLinkQueries'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { graphql } from '@/gql'
import {
  SortOrder,
  SupplierClientOrderByColumn,
  UpdateSupplierClientLinksDocument,
} from '@/gql/graphql'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const query = graphql(`
  query ClientRecruitersTableQuery(
    $accounts: [ID!]
    $client: ID!
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [SupplierClientOrderByClauseInput!]
    $supplierStatuses: [RecruiterStatus!]
    $linkedAtDateRange: DateRangeInput
  ) {
    clientSuppliers(
      accounts: $accounts
      client: $client
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      supplierStatuses: $supplierStatuses
      linkedAtDateRange: $linkedAtDateRange
    ) {
      data {
        id
        ...SupplierNameColumn
        ...SupplierPlacementsColumn
        ...SupplierLinkedAtColumn
        ...SupplierUpdatedAtColumn
        ...SupplierSinceColumn
        ...RecruiterLinkActions
      }
    }
  }
`) as TypedQueryDocumentNode

defineProps<{
  clientCompanyId: string
  clientName: string
}>()

const initialOrder = {
  field: SupplierClientOrderByColumn.SupplierName,
  order: SortOrder.Asc,
}

// The page owns the link button; the modal stays here so it renders inside the table's fullscreen element.
const linking = defineModel<boolean>('linking', { default: false })
const pendingRemoval = ref<PendingSupplierUnlink | null>(null)

const applying = ref(false)

// Select-all across pages yields an exclusion list, so a bulk action only knows the rows it was given.
async function applyLinked(rows: Record<string, object>, linked: boolean) {
  const ids = Object.values(rows as Record<string, { id?: string }>).flatMap((row) =>
    row.id ? [row.id] : [],
  )

  if (ids.length === 0 || applying.value) return

  applying.value = true

  try {
    await publicApi.mutate({
      mutation: UpdateSupplierClientLinksDocument,
      variables: { input: { ids, linked } },
      refetchQueries: SUPPLIER_LINK_QUERIES,
    })
  } finally {
    applying.value = false
  }
}

const bulkActions = computed<DataTableBulkAction[]>(() => [
  {
    name: $t('Unlink from this client'),
    icon: WdsIconNameEnum.CLOSE_OUTLINE,
    type: 'secondary',
    dataTest: 'bulk-unlink-suppliers',
    disabled: applying.value,
    action: (rows) => applyLinked(rows, false),
  },
])

</script>
